<template>
  <div class="spayg-tariff">
    <div class="mb-6">
      <p>Here's how your rates will change:</p>
    </div>
    <div class="spayg-tariff__current-rate">
      <div v-if="isLoading" class="d-flex gap-2">
        <SoSkeleton
          v-for="id in selectedMeterpointIdentifiers"
          :key="id"
          class="w-50"
          type="card"
        />
      </div>
      <SoRow v-else-if="selectedMeterpointAgreements.length > 0">
        <SoCol
          v-for="meterpointAgreement in selectedMeterpointAgreements"
          :key="meterpointAgreement.meterpoint.id"
          :default-width="12"
        >
          <SoWidget>
            <MeterpointHeader
              :meterpoint="meterpointAgreement.meterpoint"
              class="mb-4"
            />

            <SoSimpleTable
              :headers="[
                'Tariff',
                'Unit Rates',
                'Standing Charge',
                'Early Exit Fee',
              ]"
              :items="tableItems(meterpointAgreement)"
              is-pivoted
              equal-width-columns
            />
          </SoWidget>
        </SoCol>
      </SoRow>
    </div>
  </div>
</template>

<script lang="ts">
  import { PropType, defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import { TariffAgreementsWithMeterpoint } from "@/types/MeterpointTariffs"
  import { ITariff } from "@/models/tariff/tariff.types"
  import { SmartPayAsYouGoMeterPoint } from "@/api/backofficeRest/generated/model"
  import dayjs from "dayjs"

  export default defineComponent({
    components: {
      MeterpointHeader,
      SoSimpleTable,
      SoWidget,
      SoRow,
      SoCol,
      SoSkeleton,
    },

    props: {
      modelValue: {
        type: Array as PropType<SmartPayAsYouGoMeterPoint[]>,
        default: () => [],
      },
      switchDate: {
        type: [Object, String] as PropType<Date | string>,
        default: () => null,
      },
      spaygRepaymentAmount: {
        type: Number as PropType<number>,
        default: () => null,
      },
      selectedMeterpointIdentifiers: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
    },

    emits: ["update:modelValue"],

    data() {
      return {
        currentAccount: useCurrentAccountStore(),
      }
    },

    computed: {
      model: {
        get(): SmartPayAsYouGoMeterPoint[] {
          return this.modelValue
        },
        set(val: SmartPayAsYouGoMeterPoint[]) {
          this.$emit("update:modelValue", val)
        },
      },
      meterpointTariffs() {
        return this.currentAccount.meterpointTariffs
      },
      isLoading(): boolean {
        return this.meterpointTariffs.isFetching
      },
      selectedMeterpointAgreements(): TariffAgreementsWithMeterpoint[] {
        const meterpointIds = Object.keys(this.meterpointTariffs.data || [])

        return meterpointIds.reduce<TariffAgreementsWithMeterpoint[]>(
          (prev, current) => {
            const meterpointTariff = this.meterpointTariffs.data[current]

            if (
              !this.selectedMeterpointIdentifiers.includes(
                meterpointTariff.meterpoint.identifier,
              )
            )
              return prev

            return [...prev, meterpointTariff]
          },
          [],
        )
      },
    },

    watch: {
      selectedMeterpointAgreements: {
        handler: async function (val) {
          await this.$nextTick()
          this.model = val.map((agreement) => ({
            meterPointIdentifier: agreement?.meterpoint?.identifier,
            executionDate: dayjs(this.switchDate).format("YYYY-MM-DD"),
            productRef: agreement?.smartPaygTariffs?.[0]?.code,
          }))
        },
        immediate: true,
      },
    },

    methods: {
      tableRow(tariff: Partial<ITariff> = {}) {
        const tariffName = tariff.display || "-"
        const unitRates = tariff.unitRatesFormatted || "-"
        const standingCharge = tariff.productDetails?.standingCharge
          ? `${tariff.productDetails.standingCharge.toFixed(2)} p/day`
          : "-"
        const earlyExitFee = `£${(
          tariff.productDetails?.earlyTerminationCharge ?? 0
        ).toFixed(2)}`
        return [[tariffName, unitRates, standingCharge, earlyExitFee]]
      },
      tableItems(meterpointAgreement: TariffAgreementsWithMeterpoint) {
        const currentTariff = meterpointAgreement.currentTariff ?? {
          display: "No active agreement",
        }
        const spaygTariff = meterpointAgreement.smartPaygTariffs?.length
          ? meterpointAgreement.smartPaygTariffs[0]
          : { display: "No SPAYG tariff available" }

        return this.tableRow(currentTariff)
          .concat(this.tableRow(spaygTariff))
          .reverse()
      },
    },
  })
</script>
